import { useMemo, useState } from 'react'
import Modal from './Modal.jsx'
import Icon from './Icon.jsx'
import { DateRangeField } from './DatePicker.jsx'
import HoursField, { HoursMode } from './HoursField.jsx'
import TimeOffPrivateFields, { privateBody, privateFrom } from './TimeOffPrivateFields.jsx'
import { formatDate, hasHours, hoursWindowError, weekOf } from '../lib/dates.js'
import { toggleInSet } from '../lib/sets.js'

/**
 * Book several people off at once, starting from the day that was clicked.
 *
 * The other time-off dialog starts from a person and asks which days; this one starts from a
 * day and asks who. That is the direction a month view puts you in — you are looking at a
 * thin Friday three weeks out and want to mark the four people who will not be there — and
 * doing it one at a time through the day board means four visits to a different screen.
 *
 * Anyone already off across the whole chosen range is listed as such and cannot be picked
 * again: booking a second absence over the top of an existing one is never what was meant,
 * and it is the kind of duplicate nobody notices until a crew is short.
 */
export default function DayTimeOffModal({
  date,
  staff = [],
  timeOff = [],
  weekStart = 1,
  onSubmit,
  onClose,
}) {
  const thisWeek = useMemo(() => weekOf(date, weekStart), [date, weekStart])

  const [from, setFrom] = useState(date)
  const [to, setTo] = useState(date)
  const [note, setNote] = useState('')
  const [priv, setPriv] = useState(() => privateFrom(null))
  const [hours, setHours] = useState({ partial: false, startTime: '', endTime: '' })
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState(() => new Set())
  const [busy, setBusy] = useState(false)

  // Off for the ENTIRE range, not merely overlapping it — somebody off on the Monday can
  // still be booked off for the Tuesday to Friday, and refusing that would be wrong. And
  // only the whole day counts: a morning at the dentist does not make the afternoon
  // unbookable, so it does not make the person un-pickable here either.
  const alreadyOff = useMemo(() => {
    const covered = new Map()
    for (const t of timeOff) {
      // A row with hours never covers a whole range: its first and last days are partly
      // free, so somebody on one can still be booked off for the rest of it.
      if (!hasHours(t) && t.from <= from && to <= t.to) covered.set(t.staffId, t)
    }
    return covered
  }, [timeOff, from, to])

  const needle = query.trim().toLowerCase()
  const shown = useMemo(
    () => staff.filter((s) => !needle || s.name.toLowerCase().includes(needle)),
    [staff, needle],
  )

  const toggle = (id) => setPicked((prev) => toggleInSet(prev, id))

  const hoursInvalid = hoursWindowError(hours, from, to) !== null
  const invalid = !from || !to || to < from || picked.size === 0 || hoursInvalid

  async function submit(e) {
    e.preventDefault()
    if (invalid || busy) return
    setBusy(true)
    try {
      await onSubmit([...picked], {
        fromDate: from,
        toDate: to,
        note: note.trim() || null,
        startTime: hours.partial ? hours.startTime : null,
        endTime: hours.partial ? hours.endTime : null,
        ...privateBody(priv),
      })
    } finally {
      setBusy(false)
    }
  }

  const span = from === to ? formatDate(from, { withYear: true }) : `${formatDate(from)} – ${formatDate(to)}`

  return (
    <Modal title={`Time off — ${span}`} onClose={onClose} width={520}>
      <form onSubmit={submit}>
        <HoursMode {...hours} onChange={setHours} />

        <DateRangeField
          label="Dates"
          from={from}
          to={to}
          onChange={(f, t) => { setFrom(f); setTo(t) }}
          weekStart={weekStart}
          today={date}
          presets={[
            { label: 'This day', from: date, to: date },
            { label: 'This week', from: thisWeek.from, to: thisWeek.to },
          ]}
        />

        <HoursField {...hours} onChange={setHours} from={from} to={to} />

        <div className="field">
          <label>
            Who is away
            {picked.size > 0 && <span className="hint"> · {picked.size} selected</span>}
          </label>
          <div className="filter-panel-search dto-search">
            <Icon name="search" size={13} />
            <input
              type="text"
              value={query}
              placeholder="Search people…"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="dto-list">
            {shown.map((s) => {
              const off = alreadyOff.get(s.id)
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`ctx-item dto-row ${picked.has(s.id) ? 'on' : ''}`}
                  disabled={Boolean(off)}
                  title={off ? `Already off — ${off.note || 'time off'}` : undefined}
                  onClick={() => toggle(s.id)}
                >
                  <span className="ctx-item-check">
                    {picked.has(s.id) && <Icon name="check" size={13} strokeWidth={2.4} />}
                  </span>
                  <span className="dto-name">{s.name}</span>
                  {off && <span className="tag muted">Already off</span>}
                </button>
              )
            })}
            {shown.length === 0 && <div className="ctx-label">Nobody matches</div>}
          </div>
        </div>

        <div className="field">
          <label htmlFor="dto-note">Note <span className="hint">optional, shown on the board</span></label>
          <input
            id="dto-note"
            type="text"
            maxLength={120}
            value={note}
            placeholder="Training, appointment…"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <TimeOffPrivateFields idPrefix="dto" value={priv} onChange={(p) => setPriv((v) => ({ ...v, ...p }))} />
        <span className="hint">Everything here applies to everyone selected.</span>

        {to < from && <div className="notice">The end date is before the start date.</div>}

        <div className="modal-actions">
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary" disabled={invalid || busy}>
            {busy
              ? 'Saving…'
              : picked.size > 1
                ? `Book ${picked.size} people off`
                : 'Book time off'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
